/**
 * SurveySectionsContext
 * 
 * This context makes survey sections generally available for any component inside a survey page
 */
import React, { PropsWithChildren, useCallback, useContext, useState } from "react";
import type { Sections } from "../Apply";

export type SurveySectionsContextType = {
  sections: Sections
  navigateToSection: (id: string) => void,
  setNavigateToSection: (fn: SurveySectionsContextType["navigateToSection"]) => void
}

const defaults = {
  sections: { sections: [] },
  navigateToSection: () => { console.warn("navigateToSection not implemented"); },
  setNavigateToSection: () => { console.warn("setNavigateToSection not implemented"); }
};

export const SurveySectionsContext = React.createContext<SurveySectionsContextType>(defaults);

export const SurveySectionsProvider = (props: { sections: Sections } & PropsWithChildren) => {
  const [navigateToSection, setNavigateToSection] = useState<SurveySectionsContextType["navigateToSection"]>(defaults.navigateToSection);
  
  // Memoize the setter to prevent unnecessary re-renders when used as a useEffect dependency
  // this guarantees we use a functional set state, instead of just calling the setter directly with the function, which react will interpret as a functional setter 
  const setNavigateToSectionWrapper = useCallback((fn: SurveySectionsContextType["navigateToSection"]) => { 
    setNavigateToSection(() => fn);
  }, [setNavigateToSection]);
  
  const context: SurveySectionsContextType = {
    sections: props.sections,
    navigateToSection,
    setNavigateToSection: setNavigateToSectionWrapper
  }
  
  return <SurveySectionsContext.Provider value={context}>
    {props.children}
  </SurveySectionsContext.Provider>;  
}

export function useSurveySections() : SurveySectionsContextType {
  const surveySections = useContext(SurveySectionsContext);
  return surveySections;
}
